import {
  ChargeItem,
  Consent,
  Invoice,
  Patient,
  ServiceRequest,
  Specimen,
} from '@medplum/fhirtypes';
import { TFunction } from 'i18next';
import { IconSearch } from '@tabler/icons-react';
import { ARKON, EXTERNAL_CS, SYSTEMS } from '@consts/systems.consts';
import { getOrderIdSuggestions } from '../utils/fhir-helpers';
import { ConsentStatus } from '@/types/consent.types';
import { formatDate } from '../utils/common-helpers';
import {
  getAllOrderSubStates,
  getSubStateLabel,
} from '../utils/order-substates-helper';
import {
  SearchFieldDefinition,
  ResourceSearchConfig,
  FhirSearchQuery,
} from '@/components/resource-search/ResourceSearchForm.types';
import { ResourceTableColumn } from '@/components/resource-search/ResourceTable';
import { OrderUtils } from '../utils/order-table.utils';
import { getConsentStatusFromResource } from '../utils/order-utils';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';
import { ConsentStatusIndicator } from '../components/ConsentStatusIndicator';
import {
  CONSENT_PROVISION_TYPES,
  CONSENT_STATUS_TAG_CODES,
} from '@/consts/consent.consts';

export interface OrderSearchParams {
  patientName?: string;
  orderId?: string;
  testType?: string[];
  testTypeDisplay?: string[];
  sampleType?: string;
  sampleTypeDisplay?: string;
  status?: string;
  subState?: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  consentStatus?: ConsentStatus;
}

export const ORDER_SEARCH_DEFAULTS: OrderSearchParams = {
  patientName: undefined,
  orderId: undefined,
  testType: undefined,
  testTypeDisplay: undefined,
  sampleType: undefined,
  sampleTypeDisplay: undefined,
  status: undefined,
  subState: undefined,
  dateFrom: null,
  dateTo: null,
  consentStatus: undefined,
};

/** Data extracted from _include / _revinclude entries in the order search bundle. */
export interface OrderBundleData {
  specimens: Map<string, Specimen[]>;
  consents: Map<string, Consent>;
  invoices: Map<string, Invoice>;
  /** srId → true if at least one deposit ChargeItem exists for that order */
  deposits: Map<string, boolean>;
}

export const orderSearchConfig: ResourceSearchConfig<
  OrderSearchParams,
  ServiceRequest
> = {
  resourceType: 'ServiceRequest',
  buildSearchQuery: (params, page, itemsPerPage): FhirSearchQuery => {
    const query: string[][] = [
      [
        'category',
        `${SYSTEMS.SNOMED}|${EXTERNAL_CS.SNOMED.CODES.LABORATORY_PROCEDURE}`,
      ],
      ['_count', String(itemsPerPage)],
      ['_offset', String((page - 1) * itemsPerPage)],
      ['_sort', '-authored'],
      // Include associated resources in a single round-trip
      ['_include', 'ServiceRequest:patient'],
      ['_include', 'ServiceRequest:specimen'],
      ['_revinclude', 'Consent:data'],
      ['_revinclude', 'Invoice:subject'],
      ['_revinclude', 'ChargeItem:service'],
    ];

    if (params.status) {
      query.push(['status', params.status]);
    }

    if (params.testType && params.testType.length > 0) {
      query.push(['code', params.testType.join(',')]);
    }

    if (params.sampleType) {
      query.push(['_tag', `${ARKON.SD.SPECIMEN_TYPE}|${params.sampleType}`]);
    }

    // SubState filter via tag (server-side)
    if (params.subState) {
      query.push(['_tag', `${ARKON.SD.LAB_ORDER_SUBSTATE}|${params.subState}`]);
    }

    // Consent status filter — via _has reverse chaining on Consent.category
    if (params.consentStatus === 'permit') {
      query.push([
        '_has:Consent:data:category',
        `${CONSENT_STATUS_TAG_CODES.PERMIT.system}|${CONSENT_STATUS_TAG_CODES.PERMIT.code}`,
      ]);
    } else if (params.consentStatus === 'deny') {
      query.push([
        '_has:Consent:data:category',
        `${CONSENT_STATUS_TAG_CODES.DENY.system}|${CONSENT_STATUS_TAG_CODES.DENY.code}`,
      ]);
    }

    if (params.orderId) {
      query.push(['identifier', params.orderId]);
    }

    // Chaining search: filter by patient name on the server
    if (params.patientName) {
      query.push(['subject:Patient.name', params.patientName]);
    }

    if (params.dateFrom && params.dateTo) {
      const from = params.dateFrom.toISOString().split('T')[0];
      const to = params.dateTo.toISOString().split('T')[0];
      query.push(['_filter', `(authored ge ${from} and authored le ${to})`]);
    } else if (params.dateFrom) {
      query.push([
        'authored',
        `ge${params.dateFrom.toISOString().split('T')[0]}`,
      ]);
    } else if (params.dateTo) {
      query.push([
        'authored',
        `le${params.dateTo.toISOString().split('T')[0]}`,
      ]);
    }

    return query;
  },
  getResourceIdSuggestions: getOrderIdSuggestions,
  processBundleData: (bundle: any): OrderBundleData => {
    const specimens = new Map<string, Specimen[]>();
    const consents = new Map<string, Consent>();
    const invoices = new Map<string, Invoice>();
    const deposits = new Map<string, boolean>();

    const orders = (bundle.entry ?? [])
      .filter((e: any) => e.search?.mode === 'match')
      .map((e: any) => e.resource as ServiceRequest);

    for (const entry of bundle.entry ?? []) {
      if (entry.search?.mode !== 'include' || !entry.resource) {
        continue;
      }

      const resource = entry.resource;

      if (resource.resourceType === 'Specimen') {
        const specimen = resource as Specimen;
        for (const order of orders) {
          const matches = (order.specimen ?? []).some(
            (ref: { reference: string }) =>
              ref.reference === `Specimen/${specimen.id}`
          );
          if (!matches) {
            continue;
          }
          const existing = specimens.get(order.id) ?? [];
          existing.push(specimen);
          specimens.set(order.id, existing);
        }
        continue;
      }

      if (resource.resourceType === 'Consent') {
        const consent = resource as Consent;
        for (const d of consent.provision?.data ?? []) {
          const ref = d.reference?.reference;
          if (!ref?.startsWith('ServiceRequest/')) {
            continue;
          }
          const srId = ref.replace('ServiceRequest/', '');
          if (!consents.has(srId)) {
            consents.set(srId, consent);
          }
        }
        continue;
      }

      if (resource.resourceType === 'Invoice') {
        const invoice = resource as Invoice;
        const srRef = invoice.subject?.reference;
        if (!srRef?.startsWith('ServiceRequest/')) {
          continue;
        }
        const srId = srRef.replace('ServiceRequest/', '');
        if (!invoices.has(srId)) {
          invoices.set(srId, invoice);
        }
        continue;
      }

      if (resource.resourceType === 'ChargeItem') {
        const chargeItem = resource as ChargeItem;
        const isDeposit = chargeItem.code?.coding?.some(
          (c) =>
            c.system === ARKON.CS.CHARGE_ITEM.BASE &&
            c.code === ARKON.CS.CHARGE_ITEM.CODES.DEPOSIT
        );
        if (!isDeposit) {
          continue;
        }
        const srRef = chargeItem.service?.find((s) =>
          s.reference?.startsWith('ServiceRequest/')
        )?.reference;
        if (!srRef) {
          continue;
        }
        const srId = srRef.replace('ServiceRequest/', '');
        deposits.set(srId, true);
      }
    }

    return { specimens, consents, invoices, deposits };
  },
};

interface OrderFieldOptions {
  t: TFunction<'lims'>;
  searchParams: OrderSearchParams;
  onSearch: (updates: Partial<OrderSearchParams>) => void;
  hidePatientName?: boolean;
  patientNameQuery: string;
  setPatientNameQuery: (value: string) => void;
  setPatientQuery: (value: string) => void;
  patientNameOptions: string[];
  patientLoading: boolean;
  patientNameLoading: boolean;
  orderIdQuery: string;
  setOrderIdQuery: (value: string) => void;
  orderIdOptions: string[];
  orderIdLoading: boolean;
}

export function getOrderSearchFields({
  t,
  searchParams,
  onSearch,
  hidePatientName,
  patientNameQuery,
  setPatientNameQuery,
  setPatientQuery,
  patientNameOptions,
  patientLoading,
  patientNameLoading,
  orderIdQuery,
  setOrderIdQuery,
  orderIdOptions,
  orderIdLoading,
}: OrderFieldOptions): SearchFieldDefinition<OrderSearchParams>[] {
  return [
    !hidePatientName && {
      key: 'patientName',
      type: 'autocomplete',
      placeholder: t('orderList.searchPlaceholder', { ns: 'lims' }),
      icon: <IconSearch size={16} />,
      value: patientNameQuery,
      options: patientNameOptions,
      loading: patientLoading || patientNameLoading,
      onQueryChange: (val: string) => {
        setPatientQuery(val);
        setPatientNameQuery(val);
        if (!val) {
          onSearch({ patientName: undefined });
        }
      },
      onOptionSubmit: (val: string) => onSearch({ patientName: val }),
      onEnter: (val: string) => onSearch({ patientName: val }),
    },
    {
      key: 'orderId',
      type: 'autocomplete',
      placeholder: t('orderList.orderIdPlaceholder', { ns: 'lims' }),
      value: orderIdQuery,
      options: orderIdOptions,
      loading: orderIdLoading,
      onQueryChange: setOrderIdQuery,
      onOptionSubmit: (val: string) => onSearch({ orderId: val }),
      onEnter: (val: string) => onSearch({ orderId: val }),
      onChange: (val: string) => {
        if (!val) {
          onSearch({ orderId: undefined });
        }
      },
    },
    {
      key: 'testType',
      type: 'codeableConcept',
      maxValues: 10,
      placeholder: t('orderList.testTypePlaceholder', { ns: 'lims' }),
      binding: ARKON.VS.GENETIC_TESTS_EL,
      defaultValue:
        searchParams.testType && searchParams.testType.length > 0
          ? {
              coding: searchParams.testType.map((code, i) => ({
                code,
                display: searchParams.testTypeDisplay?.[i],
              })),
            }
          : undefined,
      onChange: (value: any) => {
        const codings = value?.coding ?? [];
        const codes = codings.map((c: any) => c.code).filter(Boolean);
        const displays = codings.map(
          (c: any) => c.display || c.text || undefined
        );
        onSearch({
          testType: codes.length > 0 ? codes : undefined,
          testTypeDisplay: displays.length > 0 ? displays : undefined,
        });
      },
    },
    {
      key: 'sampleType',
      type: 'codeableConcept',
      placeholder: t('specimenModal.sampleTypePlaceholder', { ns: 'lims' }),
      binding: ARKON.VS.SPECIMEN_TYPES_EL,
      defaultValue: searchParams.sampleType
        ? {
            coding: [
              {
                code: searchParams.sampleType,
                display: searchParams.sampleTypeDisplay,
              },
            ],
          }
        : undefined,
      onChange: (value: any) => {
        const code = value?.coding?.[0]?.code;
        const display = value?.coding?.[0]?.display || value?.text || undefined;
        onSearch({
          sampleType: code,
          sampleTypeDisplay: display,
        });
      },
    },
    {
      key: 'subState',
      type: 'select',
      placeholder: t('orderList.table.subStatus', { ns: 'lims' }),
      options: getAllOrderSubStates().map((subState) => ({
        value: subState,
        label: t(getSubStateLabel(subState) as never),
      })),
      value: searchParams.subState,
      onChange: (value: string | null) =>
        onSearch({ subState: value || undefined }),
    },
    {
      key: 'consentStatus',
      type: 'select',
      placeholder: t('orderList.table.consent', {
        ns: 'lims',
        defaultValue: 'Consent',
      }),
      options: [
        {
          value: CONSENT_PROVISION_TYPES.PERMIT,
          label: t('consent.display.accepted', { ns: 'lims' }),
        },
        {
          value: CONSENT_PROVISION_TYPES.DENY,
          label: t('consent.display.declined', { ns: 'lims' }),
        },
      ],
      value: searchParams.consentStatus,
      onChange: (value: string | null) =>
        onSearch({ consentStatus: (value as ConsentStatus) || undefined }),
    },
    {
      key: 'dateFrom',
      type: 'date',
      placeholder: t('orderList.fromDatePlaceholder', { ns: 'lims' }),
      value: searchParams.dateFrom,
      onChange: (value: Date | null) => onSearch({ dateFrom: value }),
    },
    {
      key: 'dateTo',
      type: 'date',
      placeholder: t('orderList.toDatePlaceholder', { ns: 'lims' }),
      value: searchParams.dateTo,
      onChange: (value: Date | null) => onSearch({ dateTo: value }),
    },
  ].filter(Boolean) as SearchFieldDefinition<OrderSearchParams>[];
}

interface OrderColumnsOptions {
  t: TFunction<'lims'>;
  patients: Map<string, Patient>;
  consents: Map<string, Consent>;
  hidePatientColumn?: boolean;
}

export function getOrderTableColumns({
  t,
  patients,
  consents,
  hidePatientColumn,
}: OrderColumnsOptions): ResourceTableColumn<ServiceRequest>[] {
  return [
    {
      key: 'orderId',
      label: t('orderList.table.orderId', { ns: 'lims' }),
      render: (order) => OrderUtils.getOrderId(order),
    },
    {
      key: 'patient',
      label: t('orderList.table.patient', { ns: 'lims' }),
      visible: !hidePatientColumn,
      render: (order) =>
        OrderUtils.getPatientName(
          order,
          patients,
          t('orderList.unknown', 'Unknown')
        ),
    },
    {
      key: 'testType',
      label: t('orderList.table.testType', { ns: 'lims' }),
      render: (order) =>
        OrderUtils.getTestName(
          order,
          t('orderList.unknownTest', 'Unknown Test')
        ),
    },
    {
      key: 'subState',
      label: t('orderList.table.subStatus', { ns: 'lims' }),
      render: (order) => (
        <StatusBadge
          status={OrderUtils.getSubStateStatus(order)}
          label={t(OrderUtils.getSubStateName(order) as never)}
        />
      ),
    },
    {
      key: 'consentStatus',
      label: t('orderList.table.consent', {
        ns: 'lims',
        defaultValue: 'Consent',
      }),
      render: (order) => {
        if (!order.id) {
          return (
            <StatusBadge
              status="unknown"
              label={t('orderList.unknown', { ns: 'lims' })}
            />
          );
        }
        const consent = consents.get(order.id);
        const status = getConsentStatusFromResource(consent);
        return <ConsentStatusIndicator status={status} />;
      },
    },
    {
      key: 'date',
      label: t('orderList.table.date', { ns: 'lims' }),
      render: (order) => formatDate(order.authoredOn),
    },
  ];
}
